// src/utils/testRace.ts
//
// Testovací závod (generálka živého bodování, docs/generalka-navod.md):
// závod s polem `testRace: true` je jen pro správce a rozhodčí. Členové ho
// nevidí v přehledu závodů (přihlášky), ve výsledcích, v kartách „živě“,
// v ročním hodnocení ani ve výběru tabule na /display (tam ho vidí jen
// správce a rozhodčí závodu – jako skrytou tabuli). Přímý odkaz na tabuli
// /display/{id} funguje (TV v hale bez přihlášení).
//
// Pravidla Firestore se nemění: závod zakládá a upravuje správce (create bez
// omezení polí, update isStaff()), takže `testRace` i značka `seed` jsou jen
// data. Skrytí je „opona“ v aplikaci jako u skrytých výsledků (livePublic) –
// závody čte každý.
//
// Značka nástroje Generálka (`seed`): {kind: "generalka", id, preset, by, at,
// complete}. Úklid (rehearsalSeed.deleteRehearsalRace) smaže jen závod se
// značkou – nikdy závod založený ve formuláři Závodů.

/** Pole závodu: testovací závod (skrytý členům). */
export const TEST_RACE_FIELD = "testRace";

/** Druh značky nástroje Generálka. */
export const REHEARSAL_SEED_KIND = "generalka";

/** Značka závodu založeného nástrojem Generálka. */
export interface RehearsalSeedMarker {
  kind: typeof REHEARSAL_SEED_KIND;
  /** Id založení (newOpId) – i v uid testovacích střelců. */
  id: string;
  /** Předvolba (rehearsalSeed.REHEARSAL_PRESETS). */
  preset: string;
  /** uid správce, který závod založil. */
  by: string;
  /** Založení doběhlo (stanoviště, rundy, střelci, rozhodčí). */
  complete: boolean;
}

type RaceData = Record<string, unknown> | null | undefined;

/** Testovací závod – členům skrytý. */
export function isTestRace(data: RaceData): boolean {
  return !!data && data[TEST_RACE_FIELD] === true;
}

/** Závod smí vidět člen v seznamech (přehled, výsledky, hodnocení, karty živě). */
export function visibleToMembers(data: RaceData): boolean {
  return !isTestRace(data);
}

/**
 * Značka nástroje Generálka, nebo null – jen u testovacího závodu se
 * značkou `seed.kind === "generalka"` a neprázdným `seed.id`.
 */
export function rehearsalSeedOf(data: RaceData): RehearsalSeedMarker | null {
  if (!isTestRace(data)) return null;
  const seed = (data as Record<string, unknown>).seed;
  if (!seed || typeof seed !== "object") return null;
  const s = seed as Record<string, unknown>;
  if (s.kind !== REHEARSAL_SEED_KIND || typeof s.id !== "string" || s.id.trim() === "") return null;
  return {
    kind: REHEARSAL_SEED_KIND,
    id: s.id,
    preset: typeof s.preset === "string" ? s.preset : "",
    by: typeof s.by === "string" ? s.by : "",
    complete: s.complete === true,
  };
}

/** Závod založený nástrojem Generálka (jen takový smí nástroj uklidit). */
export const isRehearsalRace = (data: RaceData): boolean => rehearsalSeedOf(data) !== null;
